import { FormProvider, SubmitErrorHandler, SubmitHandler, useForm } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';
import { NetDebtItemDto, EquityBridgeColumnDto } from '@app/shared/models/contracts/project-dto';
import { EquityBridgeColumnRole } from '@app/shared/models/contracts/enums/shared-enums';
import { useAppDispatch, useAppSelector } from '@core/hooks/redux-hooks';
import * as projectActions from '@app/core/store/project-slice';
import { cloneToMutableData } from '@app/shared/helpers';
import { Card } from '@app/shared/components/card/Card';
import { formConfigBase } from '@app/shared/constants/form-config-base';
import useSetSelectedTab from '@app/core/hooks/useSetSelectedTab';
import { RouteConstants } from '@app/modules/projects/RouteConstants';
import { setCapitalStructureSelectedTab } from '@app/core/store/ui-values-slice';
import useTriggerEffect from '@app/core/hooks/useTriggerEffect';
import Button from '@app/shared/components/button/Button';
import { ButtonAppearance, ButtonSize } from '@app/shared/components/button/button-enums';
import PlusSvg from '@app/shared/icons/Plus';
import styles from './equity-bridge-content.module.scss';
import { l } from '@locale/setupI18n';
import EquityBridgeTable from './EquityBridgeTable';

export interface EquityBridgeFormValues {
  columns: EquityBridgeColumnDto[];
  netDebtItems: NetDebtItemDto[];
  commonNetDebtItemIds: string[];
}

const toNumberOrNull = (value: unknown): number | null => {
  if (value === '' || value === null || value === undefined) {
    return null;
  }
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

export const EquityBridgeContent = () => {
  useSetSelectedTab(RouteConstants.EquityBridge, setCapitalStructureSelectedTab);

  const dispatch = useAppDispatch();
  const project = useAppSelector((state) => state.project.projectDraft);
  const { equityBridge } = project;
  const equityBridgeColumns = equityBridge?.columns;
  const netDebtItems = equityBridge?.netDebtItems;

  const initialColumns = cloneToMutableData(equityBridgeColumns ?? []);

  const formMethods = useForm<EquityBridgeFormValues>({
    ...formConfigBase,
    defaultValues: {
      columns: initialColumns,
      netDebtItems: netDebtItems?.length ? cloneToMutableData(netDebtItems) : [],
      commonNetDebtItemIds: equityBridge?.commonNetDebtItemIds ?? [],
    },
  });

  const { handleSubmit, setValue, getValues, trigger } = formMethods;

  useTriggerEffect(trigger);

  const addColumn = () => {
    const newColumn: EquityBridgeColumnDto = {
      columnId: uuidv4(),
      role: EquityBridgeColumnRole.Intermediate,
      dateMemo: '',
      equityValue: null,
      cashValue: null,
      enterpriseValue: null,
      netDebtValues: Object.fromEntries(
        getValues('netDebtItems').map((item) => [item.netDebtItemId, null])
      ),
    };
    const existingColumns = getValues('columns');
    existingColumns.splice(existingColumns.length - 1, 0, newColumn);
    setValue('columns', existingColumns);
    submitData();
  };

  const submitData = async () => {
    const formData = getValues();

    const columns: EquityBridgeColumnDto[] = formData.columns.map((column) => ({
      ...column,
      equityValue: toNumberOrNull(column.equityValue),
      cashValue: toNumberOrNull(column.cashValue),
      enterpriseValue: toNumberOrNull(column.enterpriseValue),
      netDebtValues: Object.fromEntries(
        Object.entries(column.netDebtValues ?? {}).map(([itemId, value]) => [
          itemId,
          toNumberOrNull(value),
        ])
      ),
    }));

    await dispatch(
      projectActions.updateProjectDraft({
        project: {
          ...project,
          equityBridge: {
            columns,
            netDebtItems: [...cloneToMutableData(formData.netDebtItems)],
            commonNetDebtItemIds: [...formData.commonNetDebtItemIds],
          },
        },
      })
    ).unwrap();
  };

  return (
    <Card>
      <FormProvider {...formMethods}>
        <form
          onBlur={handleSubmit(
            submitData as SubmitHandler<EquityBridgeFormValues>,
            submitData as SubmitErrorHandler<EquityBridgeFormValues>
          )}>
          <div className={styles['layout']}>
            <div className={styles['toolbar']}>
              <Button
                appearance={ButtonAppearance.DEFAULT_TERTIARY}
                size={ButtonSize.TABLE_ACTION}
                startIcon={<PlusSvg />}
                onClick={addColumn}>
                {l('_AddMemoColumn')}
              </Button>
            </div>
            <EquityBridgeTable
              onChange={submitData}
              investmentDate={project.investmentDate}
              valuationDate={project.valuationDate}
            />
          </div>
        </form>
      </FormProvider>
    </Card>
  );
};
